import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { 
  Check, Calendar, Clock, ArrowRight, ArrowLeft, CheckCircle, 
  AlertCircle, Home, TreePine, Wrench, Truck, FileText, Car, Building,
  Upload, Image, Plus, Minus, X, Sparkles, Phone, RefreshCw, Camera
} from 'lucide-react';
import { services } from '../mock';
import { Link } from 'react-router-dom';

// Guide character component - Flat design style with green theme
const GuideCharacter = ({ message }) => {
  return (
    <div className="flex items-start gap-3 bg-gradient-to-r from-[#122b26]/5 to-[#153254]/5 rounded-xl p-3 mb-4 border border-[#122b26]/10">
      <div className="flex-shrink-0">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden border-2 border-[#122b26] shadow-md bg-gradient-to-b from-[#f0f4f3] to-[#e8f0ed]">
          <svg width="100%" height="100%" viewBox="0 0 64 64">
            <ellipse cx="32" cy="60" rx="22" ry="12" fill="#122b26"/>
            <ellipse cx="32" cy="58" rx="18" ry="10" fill="#122b26"/>
            <rect x="27" y="42" width="10" height="10" fill="#f5dcc3"/>
            <circle cx="32" cy="28" r="18" fill="#f5dcc3"/>
            <ellipse cx="32" cy="18" rx="16" ry="10" fill="#3d3d3d"/>
            <path d="M18 26 Q20 10 32 12 Q44 10 46 26 Q40 18 32 17 Q24 18 18 26" fill="#3d3d3d"/>
            <circle cx="26" cy="28" r="2.5" fill="#3d3d3d"/>
            <circle cx="38" cy="28" r="2.5" fill="#3d3d3d"/>
            <circle cx="26.5" cy="27.5" r="0.8" fill="white"/>
            <circle cx="38.5" cy="27.5" r="0.8" fill="white"/>
            <path d="M22 23 Q26 21 29 23" stroke="#4a4a4a" strokeWidth="1.2" fill="none"/>
            <path d="M35 23 Q38 21 42 23" stroke="#4a4a4a" strokeWidth="1.2" fill="none"/>
            <path d="M26 35 Q32 40 38 35" stroke="#c9a08a" strokeWidth="2" fill="none" strokeLinecap="round"/>
            <circle cx="20" cy="32" r="3" fill="#f5c4b8" opacity="0.5"/>
            <circle cx="44" cy="32" r="3" fill="#f5c4b8" opacity="0.5"/>
            <path d="M25 50 L32 54 L39 50" stroke="#0e2320" strokeWidth="2.5" fill="none"/>
          </svg>
        </div>
      </div>
      <div className="flex-1 relative">
        <div className="absolute left-0 top-3 w-0 h-0 border-t-6 border-t-transparent border-b-6 border-b-transparent border-r-6 border-r-white -ml-1.5"></div>
        <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
          <p className="text-[#153254] text-sm leading-relaxed">{message}</p>
        </div>
      </div>
    </div>
  );
};

// Simplified Intervenant Card - Only name and photo, 3 people (1 woman, 2 men)
const IntervenantCard = ({ intervenant, selected, onSelect }) => {
  // Real photos of the team - HD versions
  const getPhotoUrl = (id) => {
    const photos = {
      1: 'https://customer-assets.emergentagent.com/job_a72f3521-3152-44ec-800d-97dc3c085d7c/artifacts/mlu2qlra_CHRISTIE%20%281%29.png',
      2: 'https://customer-assets.emergentagent.com/job_a72f3521-3152-44ec-800d-97dc3c085d7c/artifacts/avduy08n_FRANCK%20%281%29.png',
      3: 'https://customer-assets.emergentagent.com/job_a72f3521-3152-44ec-800d-97dc3c085d7c/artifacts/lm2erv75_JOSUE%20%281%29.png'
    };
    return photos[id];
  };

  return (
    <div
      onClick={onSelect}
      className={`cursor-pointer p-3 rounded-xl border-2 transition-all duration-300 ${
        selected 
          ? 'border-[#0A2320] bg-[#0A2320]/5 shadow-lg scale-105' 
          : 'border-gray-200 hover:border-[#0A2320]/50 hover:shadow-md bg-white'
      }`}
    >
      <div className={`w-14 h-14 md:w-16 md:h-16 mx-auto rounded-full overflow-hidden border-3 transition-all mb-2 ${
        selected ? 'border-[#0A2320] shadow-lg' : 'border-gray-200'
      }`}>
        <img 
          src={getPhotoUrl(intervenant.id)} 
          alt={intervenant.name}
          className="w-full h-full object-cover"
        />
      </div>
      <h4 className="font-bold text-[#153254] text-center text-sm md:text-base">{intervenant.name}</h4>
      {selected && (
        <div className="mt-2 flex justify-center">
          <div className="bg-[#0A2320] text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
            <Check className="h-3 w-3" />
            Sélectionné
          </div>
        </div>
      )}
    </div>
  );
};

// Simplified intervenants - 3 people only (1 woman, 2 men)
const intervenants = [
  { id: 1, name: 'Christie' },
  { id: 2, name: 'Franck' },
  { id: 3, name: 'Josué' },
];

// Generate available slots - Mon-Fri 8h-17h, Sat-Sun 8h-16h
const generateAvailableSlots = () => {
  const slots = [];
  const now = new Date();
  const minDate = new Date(now.getTime() + 48 * 60 * 60 * 1000);
  
  for (let i = 0; i < 21; i++) {
    const date = new Date(minDate);
    date.setDate(minDate.getDate() + i);
    
    const dayOfWeek = date.getDay();
    const daySlots = [];
    
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      // Weekend: 8h-16h
      daySlots.push('08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00');
    } else {
      // Weekdays: 8h-17h
      daySlots.push('08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00');
    }
    
    slots.push({
      date: date.toISOString().split('T')[0],
      dayName: date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }),
      dayShort: date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' }),
      times: daySlots,
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6
    });
  }
  
  return slots;
};

// Service icons mapping
const serviceIcons = {
  menage: Home,
  jardinage: TreePine,
  bricolage: Wrench,
  demenagement: Truck,
  administratif: FileText,
  vehicule: Car,
  airbnb: Building,
};

// Service-specific configurations
const serviceConfigs = {
  jardinage: {
    hasHours: false,
    requiresPhoto: true,
    options: [
      { id: 'taille_haies', name: 'Taillage de haies', hasDimension: true, dimensionLabel: 'Mètres linéaires', dimensionUnit: 'm' },
      { id: 'tonte', name: 'Tonte de pelouse', hasDimension: true, dimensionLabel: 'Surface', dimensionUnit: 'm²' },
      { id: 'desherbage', name: 'Désherbage', hasDimension: true, dimensionLabel: 'Surface', dimensionUnit: 'm²' },
      { id: 'ramassage', name: 'Ramassage de feuilles/déchets', hasDimension: false },
      { id: 'arrosage', name: 'Arrosage', hasDimension: false },
      { id: 'plantation', name: 'Plantation', hasDimension: false },
    ]
  },
  menage: {
    hasHours: true,
    requiresPhoto: false,
    minHours: 2,
    maxHours: 8,
    hasFrequencyChoice: true, // Ponctuel ou Régulier
    includedServices: [
      'Entretien complet des sols (aspiration et lavage)',
      'Dépoussiérage minutieux de toutes les surfaces',
      'Rangement soigné de vos espaces'
    ],
    options: [
      { id: 'vitres', name: 'Nettoyage des vitres' },
      { id: 'profondeur', name: 'Nettoyage en profondeur', hasSubOptions: true },
    ],
    deepCleaningSubOptions: [
      { id: 'canape', name: 'Canapé' },
      { id: 'tapis', name: 'Tapis' },
      { id: 'chaises', name: 'Chaises' },
    ]
  },
  vehicule: {
    hasHours: false,
    requiresPhoto: false,
    cleaningTypes: [
      { id: 'interieur', name: 'Intérieur', icon: '🧹', description: 'Aspiration, plastiques, extraction sièges' },
      { id: 'exterieur', name: 'Extérieur', icon: '🚿', description: 'Carrosserie, jantes, vitres' },
      { id: 'complet', name: 'Intérieur + Extérieur', icon: '✨', description: 'Formule complète' },
    ],
    vehicleTypes: [
      { id: 'petit', name: 'Petit véhicule', description: 'Mini, 2 places' },
      { id: 'standard', name: 'Véhicule standard', description: 'Berline, citadine, compacte' },
      { id: 'gros', name: 'Gros véhicule', description: '4x4, 7 places, camionnette' },
    ],
    requiresVehicleInfo: true, // Marque et modèle obligatoires
    hasAnimalHairOption: true  // Option poils d'animaux
  },
  bricolage: {
    hasHours: true,
    requiresPhoto: true,
    minHours: 1,
    maxHours: 8,
    options: [
      { id: 'montage', name: 'Montage de meubles' },
      { id: 'tringle', name: 'Pose de tringle à rideaux' },
      { id: 'peinture', name: 'Travaux de peinture' },
      { id: 'etageres', name: 'Fixation d\'étagères' },
      { id: 'plomberie', name: 'Petite plomberie' },
      { id: 'electricite', name: 'Petite électricité' },
    ]
  },
  demenagement: {
    hasHours: true,
    requiresPhoto: false,
    minHours: 2,
    maxHours: 8,
    notice: 'Ce service comprend uniquement le port et la préparation des cartons ainsi que l\'aide au chargement. Le déménagement de meubles lourds n\'est pas inclus.',
    options: []
  },
  administratif: {
    hasHours: true,
    requiresPhoto: false,
    minHours: 1,
    maxHours: 4,
    options: [
      { id: 'papiers', name: 'Gestion des papiers' },
      { id: 'demarches', name: 'Démarches en ligne' },
      { id: 'classement', name: 'Classement et archivage' },
      { id: 'scan', name: 'Impression / Scan' },
      { id: 'factures', name: 'Aide aux factures' },
    ]
  },
  airbnb: {
    hasHours: false,
    requiresPhoto: false,
    hasFrequencyChoice: true, // Ponctuel ou Régulier
    hasOnDemandOption: true, // Option "à la demande" uniquement pour Airbnb
    notice: 'Ce service nécessite une étude personnalisée. Nous vous recontacterons pour établir un devis adapté.',
    options: []
  }
};

// Days of week constant - used in multiple places
const daysOfWeek = [
  { id: 'lundi', label: 'Lundi' },
  { id: 'mardi', label: 'Mardi' },
  { id: 'mercredi', label: 'Mercredi' },
  { id: 'jeudi', label: 'Jeudi' },
  { id: 'vendredi', label: 'Vendredi' },
  { id: 'samedi', label: 'Samedi' },
  { id: 'dimanche', label: 'Dimanche' }
];

export const PremiumQuoteTunnel = () => {
  const [searchParams] = useSearchParams();
  const preselectedService = searchParams.get('service');
  
  const [step, setStep] = useState(1);
  const [selectedServices, setSelectedServices] = useState(preselectedService ? [preselectedService] : []);
  const [serviceDetails, setServiceDetails] = useState({});
  const [selectedIntervenant, setSelectedIntervenant] = useState(null);
  const [appointments, setAppointments] = useState({});
  const [contactInfo, setContactInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    message: ''
  });
  const [contactErrors, setContactErrors] = useState({});
  const [contactTouched, setContactTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  
  // New states for ponctuel/régulier flow
  const [frequencyType, setFrequencyType] = useState({}); // 'ponctuel' or 'regulier' per service
  const [hasFirstDate, setHasFirstDate] = useState({}); // true/false per service
  const [recurringDays, setRecurringDays] = useState({}); // array of days per service
  const [recurringTimes, setRecurringTimes] = useState({}); // times per day per service: { serviceId: { lundi: '09:00', ... } }
  const [isOnDemand, setIsOnDemand] = useState({}); // for Airbnb
  
  // Available time slots for recurring days
  const timeSlots = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];
  
  // Function to change step and scroll to top immediately
  const goToStep = (newStep) => {
    setStep(newStep);
    // Force immediate scroll to top
    window.scrollTo(0, 0);
    // Also try smooth scroll after a tiny delay for better UX
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 50);
  };
  
  const availableSlots = useMemo(() => generateAvailableSlots(), []);

  // Format an ISO date (YYYY-MM-DD) as "Jour N mois" en français
  const formatFrenchDate = (isoDate) => {
    if (!isoDate) return '';
    const d = new Date(isoDate + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  // API backend URL (from env, fallback to same origin)
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Disponibilités fetchées depuis le backend, par service
  // Forme: { [serviceId]: { datesSet: Set<string>, slotsByDate: {[date]: string[]} } }
  const [serviceAvailability, setServiceAvailability] = useState({});

  // Fetch availability when services or their options change
  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      const next = {};
      await Promise.all(selectedServices.map(async (sid) => {
        try {
          const opts = serviceDetails[sid] || {};
          const params = new URLSearchParams({
            service_id: sid,
            days_to_check: '60',
            options: JSON.stringify(opts || {})
          });
          const res = await fetch(`${API_URL}/api/availability?${params.toString()}`);
          if (!res.ok) return;
          const data = await res.json();
          const slotsByDate = {};
          const datesSet = new Set();
          (data.available_dates || []).forEach(d => {
            slotsByDate[d.date] = d.slots || [];
            datesSet.add(d.date);
          });
          const holidaysSet = new Set(
            (data.available_dates || [])
              .filter(d => d.is_holiday)
              .map(d => d.date)
          );
          next[sid] = { datesSet, slotsByDate, holidaysSet, config: data.config };
        } catch (e) {
          console.warn('availability fetch failed for', sid, e);
        }
      }));
      if (!cancelled) setServiceAvailability(next);
    };
    if (selectedServices.length > 0) fetchAll();
    else setServiceAvailability({});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedServices), JSON.stringify(selectedServices.map(sid => ({
    sid,
    cleaningType: serviceDetails[sid]?.cleaningType,
    tasks: serviceDetails[sid]?.tasks,
    selectedOptions: serviceDetails[sid]?.selectedOptions
  })))]);

  // Auto-jump calendar to the first month with availability for each service
  useEffect(() => {
    Object.entries(serviceAvailability).forEach(([sid, avail]) => {
      if (!avail || !avail.datesSet || avail.datesSet.size === 0) return;
      const apt = appointments[sid];
      // Only auto-jump if no view month chosen and no date picked
      if (apt && (apt.viewMonth !== undefined || apt.date)) return;
      const firstDate = [...avail.datesSet].sort()[0];
      if (!firstDate) return;
      const [y, m] = firstDate.split('-').map(n => parseInt(n, 10));
      setAppointments(prev => ({
        ...prev,
        [sid]: { ...(prev[sid] || {}), viewMonth: m - 1, viewYear: y }
      }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceAvailability]);
  
  // Initialize service details when services are selected
  useEffect(() => {
    const newDetails = { ...serviceDetails };
    selectedServices.forEach(serviceId => {
      if (!newDetails[serviceId]) {
        const config = serviceConfigs[serviceId] || {};
        newDetails[serviceId] = {
          hours: config.minHours || 2,
          selectedOptions: [],
          dimensions: {},
          photos: [],
          cleaningType: '',
          vehicleType: '',
          vehicleBrand: '',
          vehicleModel: '',
          hasAnimalHair: false
        };
      }
    });
    // Remove details for deselected services
    Object.keys(newDetails).forEach(key => {
      if (!selectedServices.includes(key)) {
        delete newDetails[key];
      }
    });
    setServiceDetails(newDetails);
  }, [selectedServices]);
  
  // Guide messages
  const guideMessages = {
    1: "Bonjour et bienvenue ! 👋 Je suis là pour vous accompagner. Sélectionnez un ou plusieurs services selon vos besoins.",
    2: "Parfait ! Précisez maintenant les détails de chaque service sélectionné.",
    3: "Planifions vos interventions. Choisissez un créneau pour chaque service.",
    4: "Dernière étape ! Renseignez vos coordonnées pour finaliser votre demande."
  };
  
  const toggleService = (serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };
  
  const updateServiceDetail = (serviceId, field, value) => {
    setServiceDetails(prev => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        [field]: value
      }
    }));
  };
  
  const toggleOption = (serviceId, optionId) => {
    const current = serviceDetails[serviceId]?.selectedOptions || [];
    const updated = current.includes(optionId)
      ? current.filter(id => id !== optionId)
      : [...current, optionId];
    updateServiceDetail(serviceId, 'selectedOptions', updated);
  };
  
  const handlePhotoUpload = (serviceId, e) => {
    const files = Array.from(e.target.files);
    const currentPhotos = serviceDetails[serviceId]?.photos || [];
    updateServiceDetail(serviceId, 'photos', [...currentPhotos, ...files.slice(0, 3 - currentPhotos.length)]);
  };
  
  const removePhoto = (serviceId, index) => {
    const currentPhotos = serviceDetails[serviceId]?.photos || [];
    updateServiceDetail(serviceId, 'photos', currentPhotos.filter((_, i) => i !== index));
  };
  
  const handleSubmit = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Collect all photos from services that require them
      const allPhotos = [];
      selectedServices.forEach(serviceId => {
        const photos = serviceDetails[serviceId]?.photos || [];
        photos.forEach(photo => {
          // New format: {file, preview}
          if (photo && typeof photo === 'object' && photo.file instanceof File) {
            allPhotos.push(photo.file);
          } else if (photo instanceof File) {
            // Legacy format for backward compat
            allPhotos.push(photo);
          }
        });
      });
      
      // Upload photos first if any
      let photoIds = [];
      if (allPhotos.length > 0) {
        const formData = new FormData();
        allPhotos.forEach(photo => {
          formData.append('files', photo);
        });
        
        try {
          const uploadResponse = await fetch(`${API_URL}/api/upload-quote-photos`, {
            method: 'POST',
            body: formData
          });
          
          if (uploadResponse.ok) {
            const uploadResult = await uploadResponse.json();
            photoIds = uploadResult.files.map(f => f.id);
            console.log('Photos uploaded:', photoIds);
          } else {
            console.warn('Photo upload failed, continuing without photos');
          }
        } catch (uploadError) {
          console.warn('Photo upload error:', uploadError);
        }
      }
      
      // Préparer les données des services (avec date/heure spécifique à chaque service)
      const servicesData = selectedServices.map(serviceId => {
        const apt = appointments[serviceId] || {};
        const opts = { ...(serviceDetails[serviceId] || {}) };
        if (apt.date) opts.date = apt.date;
        if (apt.time) opts.time = apt.time;
        return { service_id: serviceId, options: opts };
      });
      
      // Trouver la première date/heure de rendez-vous
      const firstAppointment = Object.values(appointments)[0];
      
      const payload = {
        services: servicesData,
        preferred_date: firstAppointment?.date || null,
        preferred_time: firstAppointment?.time || null,
        first_name: contactInfo.firstName,
        last_name: contactInfo.lastName,
        email: contactInfo.email,
        phone: contactInfo.phone,
        address: contactInfo.address,
        city: contactInfo.city,
        message: contactInfo.message || null,
        photo_ids: photoIds.length > 0 ? photoIds : null
      };
      
      const response = await fetch(`${API_URL}/api/devis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de l\'envoi du devis');
      }
      
      console.log('Quote submitted successfully:', data);
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error(error.message || 'Erreur lors de l\'envoi. Veuillez réessayer.');
    }
  };
  
  // Validation helpers
  const canProceedStep1 = selectedServices.length > 0;
  
  const canProceedStep2 = () => {
    return selectedServices.every(serviceId => {
      const config = serviceConfigs[serviceId];
      const details = serviceDetails[serviceId] || {};
      
      // Check required photos
      if (config?.requiresPhoto && (!details.photos || details.photos.length === 0)) {
        return false;
      }
      
      // Check vehicle specific
      if (serviceId === 'vehicule') {
        if (!details.cleaningType || !details.vehicleType) return false;
        // Marque et modèle obligatoires
        if (!details.vehicleBrand || details.vehicleBrand.trim() === '') return false;
        if (!details.vehicleModel || details.vehicleModel.trim() === '') return false;
      }
      
      // Check jardinage options
      if (serviceId === 'jardinage' && details.selectedOptions?.length === 0) {
        return false;
      }
      
      return true;
    });
  };
  
  const canProceedStep3 = () => {
    return selectedServices.every(serviceId => {
      const config = serviceConfigs[serviceId] || {};
      const apt = appointments[serviceId];
      
      // For services with frequency choice (menage, airbnb)
      if (config.hasFrequencyChoice) {
        const freq = frequencyType[serviceId];
        if (!freq) return false; // Must choose ponctuel or regulier
        
        if (freq === 'ponctuel') {
          // Need date and time
          return apt && apt.date && apt.time;
        } else if (freq === 'regulier') {
          const hasFirst = hasFirstDate[serviceId];
          if (hasFirst === undefined) return false; // Must answer yes/no
          
          // If yes to first date, need date and time
          if (hasFirst === true && (!apt || !apt.date || !apt.time)) return false;
          
          // Need recurring days or on-demand selected
          const days = recurringDays[serviceId] || [];
          const onDemand = isOnDemand[serviceId];
          return days.length > 0 || onDemand;
        }
      }
      
      // Standard services need date and time
      return apt && apt.date && apt.time;
    });
  };
  
  const canProceedStep4 = contactInfo.firstName && contactInfo.lastName && contactInfo.email && contactInfo.phone && contactInfo.address && contactInfo.city;
  
  // Validation pour les champs de contact
  const validateContactField = (name, value) => {
    switch (name) {
      case 'firstName':
        if (!value.trim()) return 'Le prénom est requis';
        if (value.length < 2) return 'Le prénom doit contenir au moins 2 caractères';
        return '';
      case 'lastName':
        if (!value.trim()) return 'Le nom est requis';
        if (value.length < 2) return 'Le nom doit contenir au moins 2 caractères';
        return '';
      case 'email':
        if (!value.trim()) return 'L\'email est requis';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Veuillez entrer un email valide';
        return '';
      case 'phone':
        if (!value.trim()) return 'Le téléphone est requis';
        if (!/^(0[1-9])[\s.-]?([0-9]{2}[\s.-]?){4}$/.test(value.replace(/\s/g, ''))) {
          return 'Numéro invalide (ex: 06 12 34 56 78)';
        }
        return '';
      case 'address':
        if (!value.trim()) return 'L\'adresse est requise';
        if (value.length < 5) return 'Veuillez entrer une adresse complète';
        return '';
      case 'city':
        if (!value.trim()) return 'La ville est requise';
        return '';
      default:
        return '';
    }
  };

  const handleContactChange = (field, value) => {
    setContactInfo(prev => ({ ...prev, [field]: value }));
    if (contactTouched[field]) {
      setContactErrors(prev => ({ ...prev, [field]: validateContactField(field, value) }));
    }
  };

  const handleContactBlur = (field) => {
    setContactTouched(prev => ({ ...prev, [field]: true }));
    setContactErrors(prev => ({ ...prev, [field]: validateContactField(field, contactInfo[field]) }));
  };

  // Composant pour afficher les erreurs
  const ContactErrorMessage = ({ error }) => {
    if (!error) return null;
    return (
      <div className="flex items-center gap-1 mt-1 text-red-500 text-sm">
        <AlertCircle className="h-4 w-4" />
        <span>{error}</span>
      </div>
    );
  };
  
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F4F7F6]">
        <div className="bg-gradient-to-br from-[#153254] to-[#122b26] py-12">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Demande envoyée !</h1>
          </div>
        </div>
        
        <div className="container mx-auto px-4 -mt-8">
          <div className="max-w-2xl mx-auto">
            <Card className="border-0 shadow-2xl">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-[#122b26]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-10 w-10 text-[#122b26]" />
                </div>
                
                <h2 className="text-2xl font-bold text-[#153254] mb-4">Merci pour votre confiance !</h2>
                <p className="text-gray-600 mb-6">
                  Votre demande de devis a bien été enregistrée. Notre équipe vous recontactera sous 24h pour confirmer vos rendez-vous.
                </p>
                
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-amber-800 mb-1 text-sm">Important</p>
                      <p className="text-amber-700 text-xs">
                        Vos rendez-vous ne seront définitivement validés qu'après réception et signature 
                        du formulaire Ogustine (RIB, etc.) qui vous sera envoyé par email.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-[#F4F7F6] rounded-xl p-6 mb-6 text-left">
                  <h3 className="font-bold text-[#153254] mb-4">Récapitulatif</h3>
                  <div className="space-y-3 text-sm text-gray-700">
                    {selectedServices.map(serviceId => {
                      const service = services.find(s => s.id === serviceId);
                      const apt = appointments[serviceId];
                      const freq = frequencyType[serviceId];
                      const onDemand = isOnDemand[serviceId];
                      const selectedDays = recurringDays[serviceId] || [];
                      const serviceTimes = recurringTimes[serviceId] || {};
                      
                      return (
                        <div key={serviceId} className="p-3 bg-white rounded-lg">
                          <p className="font-semibold text-[#153254]">{service?.title}</p>
                          
                          {/* À la demande */}
                          {onDemand ? (
                            <p className="text-[#D4AF37]">À la demande (selon vos réservations)</p>
                          ) : freq === 'regulier' && selectedDays.length > 0 ? (
                            // Régulier with days and times
                            <div>
                              <p className="text-gray-500">Interventions régulières :</p>
                              <ul className="ml-2 text-gray-600">
                                {selectedDays.map(dayId => {
                                  const dayLabel = daysOfWeek.find(d => d.id === dayId)?.label;
                                  const time = serviceTimes[dayId];
                                  return (
                                    <li key={dayId}>• {dayLabel}{time ? ` à ${time}` : ''}</li>
                                  );
                                })}
                              </ul>
                              {apt?.date && apt?.time && (
                                <p className="text-xs text-gray-500 mt-1">
                                  1ère intervention : {formatFrenchDate(apt?.date)} à {apt?.time}
                                </p>
                              )}
                            </div>
                          ) : apt?.date && apt?.time ? (
                            // Ponctuel or standard
                            <p className="text-gray-500">
                              {formatFrenchDate(apt?.date)} à {apt?.time}
                            </p>
                          ) : null}
                        </div>
                      );
                    })}
                    <p className="pt-2"><strong>Adresse :</strong> {contactInfo.address}, {contactInfo.city}</p>
                  </div>
                </div>
                
                <Link to="/">
                  <Button className="bg-[#153254] hover:bg-[#153254]/90 text-white">
                    Retour à l'accueil
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#F4F7F6] pb-20">
      <div className="bg-gradient-to-br from-[#153254] to-[#122b26] py-8 md:py-12">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-2xl md:text-4xl font-bold text-white mb-2">Demander un devis</h1>
          <p className="text-white/80 text-sm md:text-base">Obtenez votre devis personnalisé en quelques clics</p>
        </div>
      </div>
      
      <div className="container mx-auto px-4 -mt-6">
        <div className="max-w-4xl mx-auto min-h-[60vh]">
          {/* Progress Steps */}
          <Card className="border-0 shadow-lg mb-6">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                {[
                  { num: 1, label: 'Services' },
                  { num: 2, label: 'Détails' },
                  { num: 3, label: 'Rendez-vous' },
                  { num: 4, label: 'Coordonnées' }
                ].map((s, idx) => (
                  <React.Fragment key={s.num}>
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center font-bold text-xs md:text-sm transition-all ${
                        step >= s.num 
                          ? 'bg-[#122b26] text-white' 
                          : 'bg-gray-200 text-gray-500'
                      }`}>
                        {step > s.num ? <Check className="h-4 w-4" /> : s.num}
                      </div>
                      <span className={`text-[10px] md:text-xs mt-1 hidden md:block ${step >= s.num ? 'text-[#122b26] font-medium' : 'text-gray-400'}`}>
                        {s.label}
                      </span>
                    </div>
                    {idx < 3 && (
                      <div className={`flex-1 h-0.5 mx-1 md:mx-2 rounded ${step > s.num ? 'bg-[#122b26]' : 'bg-gray-200'}`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-xl">
            <CardContent className="p-6 md:p-8">
              <GuideCharacter message={guideMessages[step]} />
              
              {/* Step 1: Multi-Service Selection */}
              {step === 1 && (
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-[#153254] mb-2">
                    De quels services avez-vous besoin ?
                  </h2>
                  <p className="text-gray-500 mb-6 text-sm">Vous pouvez sélectionner plusieurs services</p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 mb-8">
                    {services.map(service => {
                      const IconComponent = serviceIcons[service.id] || Home;
                      const isSelected = selectedServices.includes(service.id);
                      
                      return (
                        <div
                          key={service.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleService(service.id);
                          }}
                          className={`cursor-pointer p-4 rounded-xl border-2 transition-all duration-300 relative ${
                            isSelected 
                              ? 'border-[#0A2320] bg-[#0A2320]/5 shadow-lg' 
                              : 'border-gray-200 hover:border-[#0A2320]/50 hover:shadow-md bg-white'
                          }`}
                        >
                          {isSelected && (
                            <div className="absolute top-2 right-2">
                              <div className="w-6 h-6 bg-[#0A2320] rounded-full flex items-center justify-center">
                                <Check className="h-4 w-4 text-white" />
                              </div>
                            </div>
                          )}
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 mx-auto ${
                            isSelected ? 'bg-[#0A2320] text-white' : 'bg-[#F4F7F6] text-[#153254]'
                          }`}>
                            <IconComponent className="h-6 w-6" />
                          </div>
                          <h3 className={`font-semibold text-center text-sm md:text-base ${
                            isSelected ? 'text-[#0A2320]' : 'text-[#153254]'
                          }`}>
                            {service.title}
                          </h3>
                        </div>
                      );
                    })}
                  </div>
                  
                  {selectedServices.length > 0 && (
                    <div className="bg-[#122b26]/5 border border-[#122b26]/20 rounded-xl p-4 mb-6">
                      <p className="text-[#122b26] text-sm font-medium">
                        {selectedServices.length} service{selectedServices.length > 1 ? 's' : ''} sélectionné{selectedServices.length > 1 ? 's' : ''} : {' '}
                        {selectedServices.map(id => services.find(s => s.id === id)?.title).join(', ')}
                      </p>
                    </div>
                  )}
                  
                  <div className="flex justify-end">
                    <Button 
                      onClick={() => goToStep(2)}
                      disabled={!canProceedStep1}
                      className="bg-[#122b26] hover:bg-[#0e2320] text-white px-8"
                      size="lg"
                    >
                      Continuer
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Step 2: Service Details */}
              {step === 2 && (
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-[#153254] mb-6">
                    Précisez vos besoins
                  </h2>
                  
                  <div className="space-y-8">
                    {selectedServices.map(serviceId => {
                      const service = services.find(s => s.id === serviceId);
                      const config = serviceConfigs[serviceId] || {};
                      const details = serviceDetails[serviceId] || {};
                      const IconComponent = serviceIcons[serviceId] || Home;
                      
                      return (
                        <div key={serviceId} className="bg-white border border-gray-200 rounded-2xl p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-[#122b26] rounded-xl flex items-center justify-center">
                              <IconComponent className="h-5 w-5 text-white" />
                            </div>
                            <h3 className="text-lg font-bold text-[#153254]">{service?.title}</h3>
                          </div>
                          
                          {/* Notice if exists */}
                          {config.notice && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                              <p className="text-sm text-amber-800">{config.notice}</p>
                            </div>
                          )}
                          
                          {/* Included services for ménage */}
                          {config.includedServices && (
                            <div className="bg-[#122b26]/5 rounded-lg p-4 mb-4">
                              <p className="font-semibold text-[#153254] mb-2 text-sm">Inclus dans le service :</p>
                              <ul className="space-y-1">
                                {config.includedServices.map((item, idx) => (
                                  <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                                    <Check className="h-4 w-4 text-[#122b26] flex-shrink-0 mt-0.5" />
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* Vehicle specific options */}
                          {serviceId === 'vehicule' && (
                            <>
                              <div className="mb-4">
                                <p className="font-medium text-[#153254] mb-3">Type de nettoyage *</p>
                                <div className="grid grid-cols-3 gap-3">
                                  {config.cleaningTypes?.map(type => (
                                    <div
                                      key={type.id}
                                      onClick={() => updateServiceDetail(serviceId, 'cleaningType', type.id)}
                                      className={`cursor-pointer p-4 rounded-xl border-2 text-center transition-all ${
                                        details.cleaningType === type.id
                                          ? 'border-[#122b26] bg-[#122b26]/5'
                                          : 'border-gray-200 hover:border-[#122b26]/50'
                                      }`}
                                    >
                                      <span className="text-2xl mb-2 block">{type.icon}</span>
                                      <p className="font-medium text-sm text-[#153254]">{type.name}</p>
                                      <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              
                              <div className="mb-4">
                                <p className="font-medium text-[#153254] mb-3">Type de véhicule *</p>
                                <div className="grid grid-cols-2 gap-3">
                                  {config.vehicleTypes?.map(type => (
                                    <div
                                      key={type.id}
                                      onClick={() => updateServiceDetail(serviceId, 'vehicleType', type.id)}
                                      className={`cursor-pointer p-4 rounded-xl border-2 text-center transition-all ${
                                        details.vehicleType === type.id
                                          ? 'border-[#122b26] bg-[#122b26]/5'
                                          : 'border-gray-200 hover:border-[#122b26]/50'
                                      }`}
                                    >
                                      <div className="text-3xl mb-2">{type.id === 'petit' ? '🚗' : '🚙'}</div>
                                      <p className="font-medium text-[#153254]">{type.name}</p>
                                      <p className="text-xs text-gray-500">{type.description}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              
                              {/* Marque et modèle du véhicule */}
                              <div className="mb-4">
                                <p className="font-medium text-[#153254] mb-3">Marque et modèle du véhicule *</p>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-sm text-gray-600 mb-1">Marque *</label>
                                    <input
                                      type="text"
                                      placeholder="Ex: Renault, Peugeot, BMW..."
                                      value={details.vehicleBrand || ''}
                                      onChange={(e) => updateServiceDetail(serviceId, 'vehicleBrand', e.target.value)}
                                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#122b26] focus:outline-none transition-colors"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm text-gray-600 mb-1">Modèle *</label>
                                    <input
                                      type="text"
                                      placeholder="Ex: Clio, 308, X3..."
                                      value={details.vehicleModel || ''}
                                      onChange={(e) => updateServiceDetail(serviceId, 'vehicleModel', e.target.value)}
                                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#122b26] focus:outline-none transition-colors"
                                    />
                                  </div>
                                </div>
                              </div>
                              
                              {/* Option poils d'animaux */}
                              <div className="mb-4">
                                <p className="font-medium text-[#153254] mb-3">Présence de poils d'animaux ?</p>
                                <div className="grid grid-cols-2 gap-3">
                                  <div
                                    onClick={() => updateServiceDetail(serviceId, 'hasAnimalHair', false)}
                                    className={`cursor-pointer p-4 rounded-xl border-2 text-center transition-all ${
                                      details.hasAnimalHair === false
                                        ? 'border-[#122b26] bg-[#122b26]/5'
                                        : 'border-gray-200 hover:border-[#122b26]/50'
                                    }`}
                                  >
                                    <div className="text-2xl mb-1">✅</div>
                                    <p className="font-medium text-[#153254]">Non</p>
                                    <p className="text-xs text-gray-500">Pas de poils d'animaux</p>
                                  </div>
                                  <div
                                    onClick={() => updateServiceDetail(serviceId, 'hasAnimalHair', true)}
                                    className={`cursor-pointer p-4 rounded-xl border-2 text-center transition-all ${
                                      details.hasAnimalHair === true
                                        ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                                        : 'border-gray-200 hover:border-[#D4AF37]/50'
                                    }`}
                                  >
                                    <div className="text-2xl mb-1">🐾</div>
                                    <p className="font-medium text-[#153254]">Oui</p>
                                    <p className="text-xs text-gray-500">Poils d'animaux présents</p>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                          
                          {/* Options (checkboxes) - Liste verticale */}
                          {config.options && config.options.length > 0 && (
                            <div className="mb-4">
                              <p className="font-medium text-[#153254] mb-3">
                                {serviceId === 'jardinage' ? 'Prestations souhaitées *' : 'Options supplémentaires'}
                              </p>
                              <div className="space-y-2">
                                {config.options.map(option => {
                                  const isChecked = details.selectedOptions?.includes(option.id);
                                  return (
                                    <div key={option.id}>
                                      <div
                                        onClick={() => toggleOption(serviceId, option.id)}
                                        className={`cursor-pointer p-3 rounded-lg border-2 transition-all ${
                                          isChecked
                                            ? 'border-[#0A2320] bg-[#0A2320]/5'
                                            : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                      >
                                        <div className="flex items-center gap-3">
                                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                            isChecked ? 'bg-[#0A2320] border-[#0A2320]' : 'border-gray-300'
                                          }`}>
                                            {isChecked && <Check className="h-3 w-3 text-white" />}
                                          </div>
                                          <span className="text-sm text-[#153254]">{option.name}</span>
                                        </div>
                                      </div>
                                      
                                      {/* Sub-options for "Nettoyage en profondeur" */}
                                      {option.hasSubOptions && isChecked && config.deepCleaningSubOptions && (
                                        <div className="mt-3 ml-8 p-3 bg-gray-50 rounded-lg">
                                          <p className="text-sm font-medium text-[#153254] mb-2">Éléments à nettoyer :</p>
                                          <div className="flex flex-wrap gap-2">
                                            {config.deepCleaningSubOptions.map(subOption => {
                                              const isSubChecked = details.deepCleaningItems?.includes(subOption.id);
                                              return (
                                                <button
                                                  key={subOption.id}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    const current = details.deepCleaningItems || [];
                                                    const updated = isSubChecked
                                                      ? current.filter(id => id !== subOption.id)
                                                      : [...current, subOption.id];
                                                    updateServiceDetail(serviceId, 'deepCleaningItems', updated);
                                                  }}
                                                  className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                                                    isSubChecked
                                                      ? 'bg-[#0A2320] text-white'
                                                      : 'bg-white border border-gray-300 text-[#153254] hover:border-[#0A2320]'
                                                  }`}
                                                >
                                                  {subOption.name}
                                                </button>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Dimension input for jardinage */}
                                      {option.hasDimension && isChecked && (
                                        <div className="mt-2 pl-8">
                                          <Input
                                            type="number"
                                            placeholder={option.dimensionLabel}
                                            value={details.dimensions?.[option.id] || ''}
                                            onChange={(e) => updateServiceDetail(serviceId, 'dimensions', {
                                              ...details.dimensions,
                                              [option.id]: e.target.value
                                            })}
                                            className="h-9 text-sm"
                                          />
                                          <span className="text-xs text-gray-500">{option.dimensionUnit}</span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          {/* Photo upload - Required if "Nettoyage en profondeur" is selected OR service requires it */}
                          {(config.requiresPhoto || (serviceId === 'menage' && details.selectedOptions?.includes('profondeur'))) && (
                            <div className="mb-4">
                              <p className="font-medium text-[#153254] mb-2">
                                Photos de la zone à traiter *
                              </p>
                              <p className="text-sm text-gray-500 mb-3">
                                {serviceId === 'menage' 
                                  ? 'Photos obligatoires pour le nettoyage en profondeur (canapé, tapis, chaises)'
                                  : 'Ajoutez des photos pour nous aider à évaluer le travail'}
                              </p>
                              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-[#0A2320] transition-colors">
                                <input
                                  type="file"
                                  multiple
                                  accept="image/*"
                                  onChange={(e) => {
                                    const files = Array.from(e.target.files);
                                    // Stocker le File object + l'URL d'aperçu
                                    const items = files.map(file => ({
                                      file,
                                      preview: URL.createObjectURL(file)
                                    }));
                                    updateServiceDetail(serviceId, 'photos', [...(details.photos || []), ...items]);
                                  }}
                                  className="hidden"
                                  id={`photo-upload-${serviceId}`}
                                />
                                <label htmlFor={`photo-upload-${serviceId}`} className="cursor-pointer">
                                  <Camera className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                                  <span className="text-sm text-gray-600">Cliquez pour ajouter des photos</span>
                                </label>
                              </div>
                              {details.photos && details.photos.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-3">
                                  {details.photos.map((photo, idx) => (
                                    <div key={idx} className="relative w-20 h-20">
                                      <img src={photo.preview || photo} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover rounded-lg" />
                                      <button
                                        onClick={() => {
                                          const newPhotos = details.photos.filter((_, i) => i !== idx);
                                          updateServiceDetail(serviceId, 'photos', newPhotos);
                                        }}
                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Hours selector */}
                          {config.hasHours && (
                            <div className="mb-4">
                              <p className="font-medium text-[#153254] mb-3">Nombre d'heures souhaité</p>
                              <div className="flex items-center gap-4">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => updateServiceDetail(serviceId, 'hours', Math.max(config.minHours, (details.hours || config.minHours) - 1))}
                                  disabled={(details.hours || config.minHours) <= config.minHours}
                                  className="h-10 w-10 rounded-full"
                                >
                                  <Minus className="h-4 w-4" />
                                </Button>
                                <span className="text-3xl font-bold text-[#153254] w-16 text-center">
                                  {details.hours || config.minHours}h
                                </span>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => updateServiceDetail(serviceId, 'hours', Math.min(config.maxHours, (details.hours || config.minHours) + 1))}
                                  disabled={(details.hours || config.minHours) >= config.maxHours}
                                  className="h-10 w-10 rounded-full"
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="flex justify-between mt-8">
                    <Button 
                      variant="outline"
                      onClick={() => goToStep(1)}
                      className="border-[#153254] text-[#153254]"
                      size="lg"
                    >
                      <ArrowLeft className="mr-2 h-5 w-5" />
                      Retour
                    </Button>
                    <Button 
                      onClick={() => goToStep(3)}
                      disabled={!canProceedStep2()}
                      className="bg-[#122b26] hover:bg-[#0e2320] text-white px-8"
                      size="lg"
                    >
                      Continuer
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Step 3: Appointments for each service with Ponctuel/Régulier flow */}
              {step === 3 && (
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-[#153254] mb-2">
                    Planifiez vos interventions
                  </h2>
                  <p className="text-gray-500 mb-4 text-sm">
                    Définissez la fréquence et le planning de vos services
                  </p>
                  
                  <div className="space-y-6">
                    {selectedServices.map(serviceId => {
                      const service = services.find(s => s.id === serviceId);
                      const config = serviceConfigs[serviceId] || {};
                      const apt = appointments[serviceId] || {};
                      const IconComponent = serviceIcons[serviceId] || Home;
                      const hasFrequencyChoice = config.hasFrequencyChoice;
                      const currentFrequency = frequencyType[serviceId];
                      const hasFirstDateChoice = hasFirstDate[serviceId];
                      const selectedDays = recurringDays[serviceId] || [];
                      const onDemand = isOnDemand[serviceId];
                      
                      // Generate calendar data
                      const today = new Date();
                      const currentMonth = apt.viewMonth !== undefined ? apt.viewMonth : today.getMonth();
                      const currentYear = apt.viewYear !== undefined ? apt.viewYear : today.getFullYear();
                      const firstDay = new Date(currentYear, currentMonth, 1);
                      const lastDay = new Date(currentYear, currentMonth + 1, 0);
                      const startPadding = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
                      const daysInMonth = lastDay.getDate();
                      
                      const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                                         'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
                      
                      const isDateAvailable = (day) => {
                        const date = new Date(currentYear, currentMonth, day);
                        const minDate = new Date(today.getTime() + 48 * 60 * 60 * 1000);
                        if (date < minDate) return false;
                        const avail = serviceAvailability[serviceId];
                        if (!avail) return false; // pas encore chargé
                        const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        return avail.datesSet.has(dateStr);
                      };
                      
                      const formatDate = (day) => {
                        return `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      };
                      
                      const toggleDay = (dayId) => {
                        setRecurringDays(prev => {
                          const current = prev[serviceId] || [];
                          if (current.includes(dayId)) {
                            return { ...prev, [serviceId]: current.filter(d => d !== dayId) };
                          }
                          return { ...prev, [serviceId]: [...current, dayId] };
                        });
                      };
                      
                      // Check if we need to show the calendar
                      const shouldShowCalendar = !hasFrequencyChoice || 
                        currentFrequency === 'ponctuel' || 
                        (currentFrequency === 'regulier' && hasFirstDateChoice === true);
                      
                      // Check if we need to show recurring days
                      const shouldShowRecurringDays = hasFrequencyChoice && 
                        currentFrequency === 'regulier' && 
                        hasFirstDateChoice !== undefined;
                      
                      return (
                        <div key={serviceId} className="bg-white border border-gray-200 rounded-xl p-4">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-8 h-8 bg-[#0A2320] rounded-lg flex items-center justify-center">
                              <IconComponent className="h-4 w-4 text-white" />
                            </div>
                            <h3 className="text-base font-bold text-[#153254]">{service?.title}</h3>
                          </div>
                          
                          {/* Frequency choice for ménage and airbnb */}
                          {hasFrequencyChoice && (
                            <div className="mb-4">
                              <p className="font-medium text-[#153254] mb-3">Type de prestation</p>
                              <div className="grid grid-cols-2 gap-3">
                                <button
                                  onClick={() => {
                                    setFrequencyType(prev => ({ ...prev, [serviceId]: 'ponctuel' }));
                                    setHasFirstDate(prev => ({ ...prev, [serviceId]: undefined }));
                                    setIsOnDemand(prev => ({ ...prev, [serviceId]: false }));
                                  }}
                                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                                    currentFrequency === 'ponctuel'
                                      ? 'border-[#0A2320] bg-[#0A2320]/5'
                                      : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                >
                                  <Calendar className="h-5 w-5 mx-auto mb-2 text-[#153254]" />
                                  <span className="font-medium text-[#153254]">Ponctuel</span>
                                  <p className="text-xs text-gray-500 mt-1">Une seule intervention</p>
                                </button>
                                <button
                                  onClick={() => {
                                    setFrequencyType(prev => ({ ...prev, [serviceId]: 'regulier' }));
                                    setHasFirstDate(prev => ({ ...prev, [serviceId]: undefined }));
                                  }}
                                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                                    currentFrequency === 'regulier'
                                      ? 'border-[#0A2320] bg-[#0A2320]/5'
                                      : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                >
                                  <RefreshCw className="h-5 w-5 mx-auto mb-2 text-[#153254]" />
                                  <span className="font-medium text-[#153254]">Régulier</span>
                                  <p className="text-xs text-gray-500 mt-1">Interventions récurrentes</p>
                                </button>
                              </div>
                            </div>
                          )}
                          
                          {/* For Régulier: Ask if they have a first date in mind */}
                          {hasFrequencyChoice && currentFrequency === 'regulier' && hasFirstDateChoice === undefined && (
                            <div className="mb-4">
                              <p className="font-medium text-[#153254] mb-3">
                                Avez-vous une première date en vue pour la 1ère intervention ?
                              </p>
                              <div className="flex gap-3">
                                <button
                                  onClick={() => setHasFirstDate(prev => ({ ...prev, [serviceId]: true }))}
                                  className="flex-1 p-3 rounded-xl border-2 border-gray-200 hover:border-[#0A2320] transition-all font-medium text-[#153254]"
                                >
                                  Oui
                                </button>
                                <button
                                  onClick={() => setHasFirstDate(prev => ({ ...prev, [serviceId]: false }))}
                                  className="flex-1 p-3 rounded-xl border-2 border-gray-200 hover:border-[#0A2320] transition-all font-medium text-[#153254]"
                                >
                                  Non
                                </button>
                              </div>
                            </div>
                          )}
                          
                          {/* Calendar (for ponctuel, or régulier with first date) - ONLY for services with frequency choice */}
                          {hasFrequencyChoice && shouldShowCalendar && (
                            <div className="mb-4">
                              <p className="text-sm text-gray-500 mb-3 flex items-center gap-2">
                                <AlertCircle className="h-4 w-4" />
                                Créneaux disponibles à partir de 48h
                              </p>
                              
                              <div className="flex items-center justify-between mb-3">
                                <button 
                                  onClick={() => setAppointments(prev => ({
                                    ...prev,
                                    [serviceId]: { 
                                      ...prev[serviceId], 
                                      viewMonth: currentMonth === 0 ? 11 : currentMonth - 1,
                                      viewYear: currentMonth === 0 ? currentYear - 1 : currentYear
                                    }
                                  }))}
                                  className="p-2 hover:bg-gray-100 rounded-lg"
                                >
                                  <ArrowLeft className="h-4 w-4 text-gray-600" />
                                </button>
                                <span className="font-semibold text-[#153254] text-sm">
                                  {monthNames[currentMonth]} {currentYear}
                                </span>
                                <button 
                                  onClick={() => setAppointments(prev => ({
                                    ...prev,
                                    [serviceId]: { 
                                      ...prev[serviceId], 
                                      viewMonth: currentMonth === 11 ? 0 : currentMonth + 1,
                                      viewYear: currentMonth === 11 ? currentYear + 1 : currentYear
                                    }
                                  }))}
                                  className="p-2 hover:bg-gray-100 rounded-lg"
                                >
                                  <ArrowRight className="h-4 w-4 text-gray-600" />
                                </button>
                              </div>
                              
                              {/* Week days header */}
                              <div className="grid grid-cols-7 gap-1 mb-1">
                                {['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'].map((day, idx) => (
                                  <div key={idx} className="text-center text-xs font-medium text-gray-500 py-1">
                                    {day}
                                  </div>
                                ))}
                              </div>
                              
                              {/* Calendar days */}
                              <div className="grid grid-cols-7 gap-1">
                                {[...Array(startPadding)].map((_, i) => (
                                  <div key={`empty-${i}`} className="h-9" />
                                ))}
                                
                                {[...Array(daysInMonth)].map((_, i) => {
                                  const day = i + 1;
                                  const dateStr = formatDate(day);
                                  const isAvailable = isDateAvailable(day);
                                  const isSelected = apt.date === dateStr;
                                  const isHoliday = serviceAvailability[serviceId]?.holidaysSet?.has(dateStr);
                                  
                                  return (
                                    <button
                                      key={day}
                                      onClick={() => isAvailable && setAppointments(prev => ({
                                        ...prev,
                                        [serviceId]: { ...prev[serviceId], date: dateStr, time: '' }
                                      }))}
                                      disabled={!isAvailable}
                                      title={isHoliday ? 'Jour férié — ouvert' : undefined}
                                      className={`relative h-9 rounded-lg text-sm font-medium transition-all ${
                                        isSelected
                                          ? 'bg-[#0A2320] text-white'
                                          : isAvailable
                                            ? isHoliday
                                              ? 'bg-amber-50 hover:bg-amber-100 text-amber-900 ring-1 ring-amber-300'
                                              : 'bg-gray-50 hover:bg-[#0A2320]/10 text-[#153254]'
                                            : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                                      }`}
                                    >
                                      {day}
                                      {isHoliday && isAvailable && (
                                        <span className="absolute -top-1 -right-1 text-[8px] leading-none">🇫🇷</span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                              
                              {/* Time slots */}
                              {apt.date && (
                                <div className="mt-4">
                                  <p className="text-sm font-medium text-gray-700 mb-2">Choisir une heure</p>
                                  <div className="flex flex-wrap gap-2">
                                    {(serviceAvailability[serviceId]?.slotsByDate?.[apt.date] || []).map((time) => (
                                      <button
                                        key={time}
                                        onClick={() => setAppointments(prev => ({
                                          ...prev,
                                          [serviceId]: { ...prev[serviceId], time }
                                        }))}
                                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                          apt.time === time
                                            ? 'bg-[#0A2320] text-white'
                                            : 'bg-gray-100 hover:bg-gray-200 text-[#153254]'
                                        }`}
                                      >
                                        {time}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {apt.date && apt.time && (
                                <div className="mt-3 bg-[#0A2320]/5 rounded-lg p-3">
                                  <p className="text-sm text-[#0A2320] font-medium flex items-center gap-2">
                                    <CheckCircle className="h-4 w-4" />
                                    {formatFrenchDate(apt.date)} à {apt.time}
                                  </p>
                                </div>
                              )}
                              
                              {/* Urgent mention */}
                              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <p className="text-sm text-amber-800 flex items-center gap-2">
                                  <Phone className="h-4 w-4" />
                                  <span>Si cela est urgent, n'hésitez pas à nous appeler au <a href="tel:0981543842" className="font-bold underline">09 81 54 38 42</a></span>
                                </p>
                              </div>
                            </div>
                          )}
                          
                          {/* Recurring days selection (for régulier) */}
                          {shouldShowRecurringDays && (
                            <div className="mb-4">
                              <p className="font-medium text-[#153254] mb-3">
                                Jours d'intervention récurrents
                              </p>
                              <p className="text-sm text-gray-500 mb-3">
                                Sélectionnez un ou plusieurs jours et l'heure souhaitée
                              </p>
                              <div className="space-y-3">
                                {daysOfWeek.map((day) => {
                                  const isSelected = selectedDays.includes(day.id);
                                  const selectedTime = recurringTimes[serviceId]?.[day.id] || '';
                                  
                                  return (
                                    <div key={day.id} className={`rounded-xl border-2 transition-all ${
                                      isSelected
                                        ? 'border-[#0A2320] bg-[#0A2320]/5'
                                        : 'border-gray-200'
                                    }`}>
                                      <button
                                        onClick={() => {
                                          setIsOnDemand(prev => ({ ...prev, [serviceId]: false }));
                                          toggleDay(day.id);
                                          // Clear time when deselecting
                                          if (isSelected) {
                                            setRecurringTimes(prev => {
                                              const serviceTimes = { ...(prev[serviceId] || {}) };
                                              delete serviceTimes[day.id];
                                              return { ...prev, [serviceId]: serviceTimes };
                                            });
                                          }
                                        }}
                                        className="w-full p-3 flex items-center justify-between"
                                      >
                                        <span className={`font-medium ${isSelected ? 'text-[#0A2320]' : 'text-[#153254]'}`}>
                                          {day.label}
                                        </span>
                                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                          isSelected ? 'bg-[#0A2320] border-[#0A2320]' : 'border-gray-300'
                                        }`}>
                                          {isSelected && <Check className="h-3 w-3 text-white" />}
                                        </div>
                                      </button>
                                      
                                      {/* Time selection for selected day */}
                                      {isSelected && (
                                        <div className="px-3 pb-3 border-t border-[#0A2320]/10">
                                          <p className="text-xs text-gray-500 mt-2 mb-2 flex items-center gap-1">
                                            <Clock className="h-3 w-3" />
                                            Heure souhaitée
                                          </p>
                                          <div className="flex flex-wrap gap-1.5">
                                            {timeSlots.map((time) => (
                                              <button
                                                key={time}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setRecurringTimes(prev => ({
                                                    ...prev,
                                                    [serviceId]: {
                                                      ...(prev[serviceId] || {}),
                                                      [day.id]: time
                                                    }
                                                  }));
                                                }}
                                                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                                  selectedTime === time
                                                    ? 'bg-[#0A2320] text-white'
                                                    : 'bg-white hover:bg-gray-100 text-[#153254] border border-gray-200'
                                                }`}
                                              >
                                                {time}
                                              </button>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              
                              {/* On demand option - only for Airbnb */}
                              {config.hasOnDemandOption && (
                                <button
                                  onClick={() => {
                                    setIsOnDemand(prev => ({ ...prev, [serviceId]: !onDemand }));
                                    if (!onDemand) {
                                      setRecurringDays(prev => ({ ...prev, [serviceId]: [] }));
                                      setRecurringTimes(prev => ({ ...prev, [serviceId]: {} }));
                                    }
                                  }}
                                  className={`w-full mt-3 p-3 rounded-xl border-2 transition-all text-sm font-medium ${
                                    onDemand
                                      ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                                      : 'border-gray-200 hover:border-gray-300 text-[#153254]'
                                  }`}
                                >
                                  À la demande (selon les réservations)
                                </button>
                              )}
                              
                              {(selectedDays.length > 0 || onDemand) && (
                                <div className="mt-3 bg-[#0A2320]/5 rounded-lg p-3">
                                  <p className="text-sm text-[#0A2320] font-medium flex items-center gap-2">
                                    <CheckCircle className="h-4 w-4" />
                                    {onDemand 
                                      ? 'Interventions à la demande selon vos réservations'
                                      : (
                                        <span>
                                          Interventions : {selectedDays.map(d => {
                                            const dayLabel = daysOfWeek.find(day => day.id === d)?.label;
                                            const time = recurringTimes[serviceId]?.[d];
                                            return time ? `${dayLabel} à ${time}` : dayLabel;
                                          }).join(', ')}
                                        </span>
                                      )
                                    }
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Standard calendar for services without frequency choice */}
                          {!hasFrequencyChoice && (
                            <div className="mb-4">
                              <p className="text-sm text-gray-500 mb-3 flex items-center gap-2">
                                <AlertCircle className="h-4 w-4" />
                                Créneaux disponibles à partir de 48h
                              </p>
                              
                              <div className="flex items-center justify-between mb-3">
                                <button 
                                  onClick={() => setAppointments(prev => ({
                                    ...prev,
                                    [serviceId]: { 
                                      ...prev[serviceId], 
                                      viewMonth: currentMonth === 0 ? 11 : currentMonth - 1,
                                      viewYear: currentMonth === 0 ? currentYear - 1 : currentYear
                                    }
                                  }))}
                                  className="p-2 hover:bg-gray-100 rounded-lg"
                                >
                                  <ArrowLeft className="h-4 w-4 text-gray-600" />
                                </button>
                                <span className="font-semibold text-[#153254] text-sm">
                                  {monthNames[currentMonth]} {currentYear}
                                </span>
                                <button 
                                  onClick={() => setAppointments(prev => ({
                                    ...prev,
                                    [serviceId]: { 
                                      ...prev[serviceId], 
                                      viewMonth: currentMonth === 11 ? 0 : currentMonth + 1,
                                      viewYear: currentMonth === 11 ? currentYear + 1 : currentYear
                                    }
                                  }))}
                                  className="p-2 hover:bg-gray-100 rounded-lg"
                                >
                                  <ArrowRight className="h-4 w-4 text-gray-600" />
                                </button>
                              </div>
                              
                              <div className="grid grid-cols-7 gap-1 mb-1">
                                {['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'].map((day, idx) => (
                                  <div key={idx} className="text-center text-xs font-medium text-gray-500 py-1">
                                    {day}
                                  </div>
                                ))}
                              </div>
                              
                              <div className="grid grid-cols-7 gap-1">
                                {[...Array(startPadding)].map((_, i) => (
                                  <div key={`empty-${i}`} className="h-9" />
                                ))}
                                
                                {[...Array(daysInMonth)].map((_, i) => {
                                  const day = i + 1;
                                  const dateStr = formatDate(day);
                                  const isAvailable = isDateAvailable(day);
                                  const isSelected = apt.date === dateStr;
                                  const isHoliday = serviceAvailability[serviceId]?.holidaysSet?.has(dateStr);
                                  
                                  return (
                                    <button
                                      key={day}
                                      onClick={() => isAvailable && setAppointments(prev => ({
                                        ...prev,
                                        [serviceId]: { ...prev[serviceId], date: dateStr, time: '' }
                                      }))}
                                      disabled={!isAvailable}
                                      title={isHoliday ? 'Jour férié — ouvert' : undefined}
                                      className={`relative h-9 rounded-lg text-sm font-medium transition-all ${
                                        isSelected
                                          ? 'bg-[#0A2320] text-white'
                                          : isAvailable
                                            ? isHoliday
                                              ? 'bg-amber-50 hover:bg-amber-100 text-amber-900 ring-1 ring-amber-300'
                                              : 'bg-gray-50 hover:bg-[#0A2320]/10 text-[#153254]'
                                            : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                                      }`}
                                    >
                                      {day}
                                      {isHoliday && isAvailable && (
                                        <span className="absolute -top-1 -right-1 text-[8px] leading-none">🇫🇷</span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                              
                              {apt.date && (
                                <div className="mt-4">
                                  <p className="text-sm font-medium text-gray-700 mb-2">Choisir une heure</p>
                                  <div className="flex flex-wrap gap-2">
                                    {(serviceAvailability[serviceId]?.slotsByDate?.[apt.date] || []).map((time) => (
                                      <button
                                        key={time}
                                        onClick={() => setAppointments(prev => ({
                                          ...prev,
                                          [serviceId]: { ...prev[serviceId], time }
                                        }))}
                                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                          apt.time === time
                                            ? 'bg-[#0A2320] text-white'
                                            : 'bg-gray-100 hover:bg-gray-200 text-[#153254]'
                                        }`}
                                      >
                                        {time}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {apt.date && apt.time && (
                                <div className="mt-3 bg-[#0A2320]/5 rounded-lg p-3">
                                  <p className="text-sm text-[#0A2320] font-medium flex items-center gap-2">
                                    <CheckCircle className="h-4 w-4" />
                                    {formatFrenchDate(apt.date)} à {apt.time}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="flex flex-col sm:flex-row justify-between gap-3 mt-6">
                    <Button 
                      variant="outline"
                      onClick={() => goToStep(2)}
                      className="border-[#153254] text-[#153254]"
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Retour
                    </Button>
                    <Button 
                      onClick={() => goToStep(4)}
                      disabled={!canProceedStep3()}
                      className="bg-[#0A2320] hover:bg-[#0e2320] text-white px-6"
                    >
                      Continuer
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Step 4: Contact Information */}
              {step === 4 && (
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-[#153254] mb-4">
                    Vos coordonnées
                  </h2>
                  
                  {/* Stacking properly on mobile */}
                  <div className="space-y-3 mb-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                        <Input
                          value={contactInfo.firstName}
                          onChange={(e) => handleContactChange('firstName', e.target.value)}
                          onBlur={() => handleContactBlur('firstName')}
                          placeholder="Votre prénom"
                          className={`h-10 rounded-lg ${contactErrors.firstName && contactTouched.firstName ? 'border-red-500' : ''}`}
                          data-testid="quote-firstname-input"
                        />
                        {contactTouched.firstName && <ContactErrorMessage error={contactErrors.firstName} />}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                        <Input
                          value={contactInfo.lastName}
                          onChange={(e) => handleContactChange('lastName', e.target.value)}
                          onBlur={() => handleContactBlur('lastName')}
                          placeholder="Votre nom"
                          className={`h-10 rounded-lg ${contactErrors.lastName && contactTouched.lastName ? 'border-red-500' : ''}`}
                          data-testid="quote-lastname-input"
                        />
                        {contactTouched.lastName && <ContactErrorMessage error={contactErrors.lastName} />}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone *</label>
                        <Input
                          type="tel"
                          value={contactInfo.phone}
                          onChange={(e) => handleContactChange('phone', e.target.value)}
                          onBlur={() => handleContactBlur('phone')}
                          placeholder="06 12 34 56 78"
                          className={`h-10 rounded-lg ${contactErrors.phone && contactTouched.phone ? 'border-red-500' : ''}`}
                          data-testid="quote-phone-input"
                        />
                        {contactTouched.phone && <ContactErrorMessage error={contactErrors.phone} />}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                        <Input
                          type="email"
                          value={contactInfo.email}
                          onChange={(e) => handleContactChange('email', e.target.value)}
                          onBlur={() => handleContactBlur('email')}
                          placeholder="votre@email.fr"
                          className={`h-10 rounded-lg ${contactErrors.email && contactTouched.email ? 'border-red-500' : ''}`}
                          data-testid="quote-email-input"
                        />
                        {contactTouched.email && <ContactErrorMessage error={contactErrors.email} />}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Adresse d'intervention *</label>
                      <Input
                        value={contactInfo.address}
                        onChange={(e) => handleContactChange('address', e.target.value)}
                        onBlur={() => handleContactBlur('address')}
                        placeholder="Numéro et nom de rue"
                        className={`h-10 rounded-lg ${contactErrors.address && contactTouched.address ? 'border-red-500' : ''}`}
                        data-testid="quote-address-input"
                      />
                      {contactTouched.address && <ContactErrorMessage error={contactErrors.address} />}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ville / Code postal *</label>
                      <Input
                        value={contactInfo.city}
                        onChange={(e) => handleContactChange('city', e.target.value)}
                        onBlur={() => handleContactBlur('city')}
                        placeholder="60110 Méru"
                        className={`h-10 rounded-lg ${contactErrors.city && contactTouched.city ? 'border-red-500' : ''}`}
                        data-testid="quote-city-input"
                      />
                      {contactTouched.city && <ContactErrorMessage error={contactErrors.city} />}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Message (optionnel)</label>
                      <Textarea
                        value={contactInfo.message}
                        onChange={(e) => setContactInfo(prev => ({ ...prev, message: e.target.value }))}
                        placeholder="Précisions sur votre demande..."
                        rows={2}
                        className="rounded-lg"
                        data-testid="quote-message-textarea"
                      />
                    </div>
                  </div>
                  
                  <div className="bg-[#F4F7F6] rounded-xl p-6 mb-6">
                    <h3 className="font-bold text-[#153254] mb-4">
                      Récapitulatif
                    </h3>
                    <div className="space-y-3">
                      {selectedServices.map(serviceId => {
                        const service = services.find(s => s.id === serviceId);
                        const config = serviceConfigs[serviceId] || {};
                        const details = serviceDetails[serviceId] || {};
                        const apt = appointments[serviceId] || {};
                        const freq = frequencyType[serviceId];
                        const onDemand = isOnDemand[serviceId];
                        const selectedDays = recurringDays[serviceId] || [];
                        const serviceTimes = recurringTimes[serviceId] || {};
                        
                        // Skip appointment display for "À la demande" services
                        const showAppointmentInfo = !onDemand;
                        
                        return (
                          <div key={serviceId} className="bg-white rounded-lg p-4">
                            <p className="font-semibold text-[#153254]">{service?.title}</p>
                            {config.hasHours && (
                              <p className="text-sm text-gray-500">Durée : {details.hours}h</p>
                            )}
                            {details.selectedOptions?.length > 0 && (
                              <p className="text-sm text-gray-500">
                                Options : {details.selectedOptions.map(id => 
                                  config.options?.find(o => o.id === id)?.name
                                ).join(', ')}
                              </p>
                            )}
                            
                            {/* Véhicule info */}
                            {serviceId === 'vehicule' && (
                              <div className="text-sm text-gray-500 mt-1 space-y-0.5">
                                {details.cleaningType && (
                                  <p>Type de nettoyage : {
                                    details.cleaningType === 'interieur' ? 'Intérieur' :
                                    details.cleaningType === 'exterieur' ? 'Extérieur' : 'Complet'
                                  }</p>
                                )}
                                {details.vehicleType && (
                                  <p>Type de véhicule : {
                                    details.vehicleType === 'petit' ? 'Petit véhicule' :
                                    details.vehicleType === 'standard' ? 'Véhicule standard' : 'Gros véhicule'
                                  }</p>
                                )}
                                {details.vehicleBrand && details.vehicleModel && (
                                  <p className="font-medium text-[#153254]">🚗 {details.vehicleBrand} {details.vehicleModel}</p>
                                )}
                                {details.hasAnimalHair && (
                                  <p className="text-[#D4AF37] font-medium">🐾 Poils d'animaux présents</p>
                                )}
                              </div>
                            )}
                            
                            {/* Display based on frequency type */}
                            {showAppointmentInfo && (
                              <>
                                {freq === 'regulier' && selectedDays.length > 0 ? (
                                  <div className="text-sm text-[#122b26] font-medium mt-1">
                                    <p className="flex items-center gap-1 mb-1">
                                      <RefreshCw className="h-3 w-3" /> Interventions régulières :
                                    </p>
                                    <ul className="ml-4 space-y-0.5 text-gray-600">
                                      {selectedDays.map(dayId => {
                                        const dayLabel = daysOfWeek.find(d => d.id === dayId)?.label;
                                        const time = serviceTimes[dayId];
                                        return (
                                          <li key={dayId}>
                                            • {dayLabel}{time ? ` à ${time}` : ''}
                                          </li>
                                        );
                                      })}
                                    </ul>
                                    {apt.date && apt.time && (
                                      <p className="mt-1 text-xs text-gray-500">
                                        1ère intervention : {formatFrenchDate(apt.date)} à {apt.time}
                                      </p>
                                    )}
                                  </div>
                                ) : apt.date && apt.time ? (
                                  <p className="text-sm text-[#122b26] font-medium mt-1">
                                    📅 {formatFrenchDate(apt.date)} à {apt.time}
                                  </p>
                                ) : null}
                              </>
                            )}
                            
                            {/* À la demande display */}
                            {onDemand && (
                              <p className="text-sm text-[#D4AF37] font-medium mt-1 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                À la demande (selon vos réservations)
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    
                    {selectedIntervenant && (
                      <p className="text-sm text-gray-600 mt-4">
                        <strong>Intervenant préféré :</strong> {intervenants.find(i => i.id === selectedIntervenant)?.name}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex flex-col sm:flex-row justify-between gap-4">
                    <Button 
                      variant="outline"
                      onClick={() => goToStep(3)}
                      className="border-[#153254] text-[#153254]"
                      size="lg"
                    >
                      <ArrowLeft className="mr-2 h-5 w-5" />
                      Retour
                    </Button>
                    <Button 
                      onClick={handleSubmit}
                      disabled={!canProceedStep4}
                      className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white px-8 font-bold"
                      size="lg"
                    >
                      Envoyer ma demande
                      <Check className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          <div className="mt-6 text-center text-sm text-gray-500">
            <p className="flex flex-wrap items-center justify-center gap-1">
              <Phone className="h-4 w-4 flex-shrink-0" />
              <span>Besoin d'aide ?</span>
              <a href="tel:0981543842" className="text-[#153254] font-medium hover:underline whitespace-nowrap">09 81 54 38 42</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
